import {
  EDT_ACTIVITY_OPTIONS,
  EDT_DURATION_PRESETS,
  EDT_DURATION_UNIT_OPTIONS,
  HILLOS,
  MAX_EDT_DURATION_MINUTES,
  formatDuration,
  isHilloNumber,
  naturalDurationUnit,
} from '@iculogbook/shared';
import type { EdtActivity, EdtDurationUnit } from '@iculogbook/shared';
import { useState } from 'react';

import {
  Button,
  ChipGroup,
  Disclosure,
  Field,
  NO_IDENTIFIABLE_INFORMATION,
  SegmentedControl,
  TextArea,
  TextInput,
} from '../../components/index.ts';
import { OccurredAtField } from './OccurredAtField.tsx';
import type { FormErrors } from './ProcedureForm.tsx';
import { fromDateValue, toDateValue } from './datetime.ts';
import { parseDuration } from './edtForm.ts';
import type { EdtFormValues } from './edtForm.ts';

/** For the "Until 3 August" badge on the period disclosure. */
const DATE = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });

/** Minutes per unit, for seeding the amount box from a stored value. */
const UNIT_MINUTES: Record<EdtDurationUnit, number> = { minutes: 1, hours: 60, days: 24 * 60 };

const TITLE_MAX_LENGTH = 140;
const NOTE_MAX_LENGTH = 2000;

/** The preset minutes, so a custom value can be told from a chip value. */
const PRESET_MINUTES = EDT_DURATION_PRESETS.map((option) => Number(option.key));

/**
 * Placeholders by activity. A blank box invites a blank entry; a concrete
 * example shows the level of detail that makes an entry worth having, without
 * making anything required.
 */
const TITLE_PLACEHOLDERS: Record<EdtActivity, string> = {
  teaching_delivered: 'Regional teaching — ventilation in ARDS',
  teaching_attended: 'Departmental teaching — delirium',
  clinic: 'ICU follow-up clinic',
  self_directed_learning: 'Reading — ESICM sepsis guidelines',
  project_audit: 'Audit — sedation holds on the unit',
  research: 'Trial screening and recruitment',
  course_conference: 'ICS State of the Art',
  simulation: 'Sim — major haemorrhage',
  management: 'Rota and staffing meeting',
  exam_preparation: 'FFICM revision — physiology',
  other: 'What you did',
};

type EdtFormProps = {
  values: EdtFormValues;
  onChange: (values: EdtFormValues) => void;
  onSave: () => void;
  /** Saves, then opens a new form prefilled from what was just saved. */
  onSaveAndDuplicate: () => void;
  saving: boolean;
  errors: FormErrors;
  /** Editing a stored entry rather than logging a new one. */
  editing?: boolean;
};

/**
 * The EDT/Teaching form: what you did with your Educational Development Time.
 *
 * Three fields carry the entry — activity, a one-line title, and the date —
 * and all three fit on a phone screen without scrolling. The long notes box is
 * the point of the record rather than an afterthought, so it sits in the open
 * beneath them rather than behind a disclosure. Duration and curriculum tagging
 * are optional and stay out of the way until wanted.
 */
export function EdtForm({
  values,
  onChange,
  onSave,
  onSaveAndDuplicate,
  saving,
  errors,
  editing = false,
}: EdtFormProps) {
  function set<K extends keyof EdtFormValues>(key: K, value: EdtFormValues[K]): void {
    onChange({ ...values, [key]: value });
  }

  const custom =
    values.durationMinutes !== null && !PRESET_MINUTES.includes(values.durationMinutes);

  /*
   * The unit and the typed amount are UI state, not form state: only the
   * resulting minutes are stored. Seeded from whatever is already on the
   * entry, so reopening one saved as "3 days" shows 3 and Days rather than
   * 4320 and Minutes.
   *
   * `useState` initialisers, so editing them afterwards is not fought by a
   * re-render — the stored minutes remain the single source of truth and these
   * two only decide how that number is typed.
   */
  const [unit, setUnit] = useState<EdtDurationUnit>(() =>
    custom && values.durationMinutes !== null
      ? naturalDurationUnit(values.durationMinutes)
      : 'minutes',
  );
  const [amount, setAmount] = useState(() =>
    custom && values.durationMinutes !== null
      ? String(values.durationMinutes / UNIT_MINUTES[naturalDurationUnit(values.durationMinutes)])
      : '',
  );

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
      className="flex flex-col gap-4"
      noValidate
    >
      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-text">Activity</span>
        <ChipGroup
          label="Activity"
          options={EDT_ACTIVITY_OPTIONS.map((option) => ({
            value: option.key,
            label: option.label,
          }))}
          value={values.activity}
          onChange={(next) => set('activity', next)}
        />
        {errors['activity'] === undefined ? null : (
          <p role="alert" className="text-xs text-danger">
            {errors['activity']}
          </p>
        )}
      </div>

      <Field
        label="What you did"
        help={NO_IDENTIFIABLE_INFORMATION}
        error={errors['title']}
        required
      >
        <TextInput
          value={values.title}
          onChange={(event) => set('title', event.target.value)}
          maxLength={TITLE_MAX_LENGTH}
          placeholder={
            values.activity === null ? 'What you did' : TITLE_PLACEHOLDERS[values.activity]
          }
          autoFocus
          enterKeyHint="next"
        />
      </Field>

      <OccurredAtField
        value={values.occurredAt}
        onChange={(next) => set('occurredAt', next)}
        error={errors['occurredAt']}
      />

      <div className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-text">
          Time spent <span className="font-normal text-faint">(optional)</span>
        </span>
        <ChipGroup
          label="Time spent"
          options={EDT_DURATION_PRESETS.map((option) => ({
            value: option.key,
            label: option.label,
          }))}
          value={custom || values.durationMinutes === null ? null : String(values.durationMinutes)}
          onChange={(next) => {
            // Choosing a preset clears the custom box, or the two would
            // disagree on screen about what this entry claims.
            setAmount('');
            setUnit('minutes');
            set('durationMinutes', next === null ? null : Number(next));
          }}
        />

        <Disclosure
          title="Another length"
          hint="In minutes, hours or days"
          badge={custom ? formatDuration(values.durationMinutes ?? 0) : null}
        >
          {/*
            An amount and a unit, rather than minutes alone. Nobody logging a
            three-day course wants to work out that it is 4320, and the
            arithmetic is exactly the sort of friction that stops an entry
            being made. Storage is still minutes — the unit never leaves this
            form.
          */}
          <div className="flex flex-col gap-3">
            <Field label="How long" error={errors['durationMinutes']}>
              <TextInput
                type="number"
                inputMode="decimal"
                min={0}
                step="any"
                value={amount}
                onChange={(event) => {
                  const next = event.target.value;
                  setAmount(next);
                  set('durationMinutes', parseDuration(next, unit));
                }}
              />
            </Field>

            <SegmentedControl
              label="Unit"
              compact
              segments={EDT_DURATION_UNIT_OPTIONS.map((option) => ({
                value: option.key,
                label: option.label,
              }))}
              value={unit}
              onChange={(next) => {
                setUnit(next);
                // Re-read the amount in the new unit rather than converting
                // the stored minutes: typing 3 and then tapping Days means
                // three days, not three minutes expressed differently.
                set('durationMinutes', parseDuration(amount, next));
              }}
            />

            {amount.trim() !== '' && values.durationMinutes === null ? (
              <p role="alert" className="text-xs text-danger">
                {unit === 'minutes'
                  ? `Give a whole number of minutes, up to ${String(MAX_EDT_DURATION_MINUTES)}.`
                  : `That is longer than ${formatDuration(MAX_EDT_DURATION_MINUTES)}, or not a whole number of minutes. Log it as more than one entry.`}
              </p>
            ) : null}
          </div>
        </Disclosure>

        {/*
          A period rather than a moment: "20 hours of reading between May and
          August". The duration above is the work done, not the time elapsed,
          so the two are independent and both are optional.
        */}
        <Disclosure
          title="Spread over a period"
          hint="Optional. For work done across a stretch of time"
          badge={values.endsAt === null ? null : `Until ${DATE.format(values.endsAt)}`}
        >
          <Field
            label="Ends"
            error={
              values.endsAt !== null && values.endsAt.getTime() < values.occurredAt.getTime()
                ? 'That period ends before it starts.'
                : undefined
            }
          >
            <TextInput
              type="date"
              value={values.endsAt === null ? '' : toDateValue(values.endsAt)}
              onChange={(event) => set('endsAt', fromDateValue(event.target.value))}
            />
          </Field>
          <p className="mt-1.5 text-xs text-faint">
            The date above is the start. Reports count this entry once, on its start date.
          </p>
        </Disclosure>
      </div>

      {/*
        Open, not behind a disclosure. On a case or a procedure the note is an
        aside to a set of structured fields; here it is the entry. Hiding it
        would mean the common EDT entry is a title and nothing else, which is
        not evidence of anything.
      */}
      <Field label="Notes" help={NO_IDENTIFIABLE_INFORMATION} error={errors['note']}>
        <TextArea
          value={values.note}
          onChange={(event) => set('note', event.target.value)}
          maxLength={NOTE_MAX_LENGTH}
          rows={8}
          placeholder="What was covered, what you took from it, anything to follow up."
        />
      </Field>

      <Disclosure
        title="Curriculum"
        hint="Optional HiLLO tagging. Nothing is stored until you tap."
        badge={values.hillos.length === 0 ? null : `${String(values.hillos.length)} tagged`}
      >
        <ChipGroup
          label="High Level Learning Outcomes"
          multiple
          options={HILLOS.map((hillo) => ({
            value: String(hillo.number),
            label: `${String(hillo.number)}. ${hillo.shortLabel}`,
          }))}
          value={values.hillos.map(String)}
          onChange={(next) => set('hillos', next.map(Number).filter(isHilloNumber))}
        />
      </Disclosure>

      <Button type="submit" variant="primary" block disabled={saving} className="h-14">
        {editing ? 'Save changes' : 'Save entry'}
      </Button>

      <Button
        variant="secondary"
        block
        disabled={saving}
        onClick={onSaveAndDuplicate}
        className="h-14"
      >
        {editing ? 'Save changes and duplicate' : 'Save and duplicate'}
      </Button>
    </form>
  );
}
